import React, { useState, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useDispatch, connect } from 'react-redux';

import './bankAccount.css';
import { isUserLoggedIn } from '../../../../shared/utils/function';
import { useNavigation } from '../../../../shared/utils/useNavigation';
import { showSuccess, showError } from '../../../../shared/utils/swalHandler';
import ModernHeader from '../../../../components/layout/Headers/ModernHeader';
import { createDynamicSchemaForBankAccount } from '../../../../shared/validations/transaction';
import {
  PAYOUT_METHODS,
  getBankDepositPayerOptions,
  getCryptoPayerOptions,
  PayerSelectionDropdown,
  getDestinationToken,
  DynamicMasspayForm,
  extractAndFormatAccountOrWallet,
  deliveryTypeMap,
} from '../../../../shared/utils/transactionUtil';
import {
  getPaymentMethod,
  validatePaymentMethodChangeCode,
  lookupUser,
  listServices,
  getMasspayAttributes,
  createMasspayAccount
} from '../../../../shared/actions/transactionAction';
import PayoutMethodDisplay from './PayoutMethodDisplay';
import { ROUTES } from '../../../../shared/constants/routes';


const BankDetail = ({
  user,
  lookupUser: lookupUserState,
  listServices: listServicesState
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigation();
  const isLoggedIn = isUserLoggedIn();
  const [isUserHasPaymentMethod, setIsUserHasPaymentMethod] = useState(false);
  const [isLoadingPaymentMethod, setIsLoadingPaymentMethod] = useState(true);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [userToken, setUserToken] = useState(null);
  const [payoutMethod, setPayoutMethod] = useState(null);
  const [selectedPayerToken, setSelectedPayerToken] = useState('');
  const [destinationInfo, setDestinationInfo] = useState(null);
  const [masspayAttributes, setMasspayAttributes] = useState(null);
  const [isLoadingAttributes, setIsLoadingAttributes] = useState(false);

  const allAttributes = useMemo(() => {
    if (!masspayAttributes?.data || !Array.isArray(masspayAttributes.data)) {
      return [];
    }

    return masspayAttributes.data
      .filter((attrSet) => Array.isArray(attrSet?.attributes))
      .flatMap((attrSet) => attrSet.attributes);
  }, [masspayAttributes]);

  useEffect(() => {
    // First try to fetch existing payment method information
    const initializePaymentAndLookup = async () => {
      setIsLoadingPaymentMethod(true);
      const runLookup = () => {
        setIsUserHasPaymentMethod(false);
        dispatch(lookupUser());
        dispatch(listServices());
      };

      try {
        const { pathname = '', search = '' } = window.location || {};
        const code = new URLSearchParams(search).get('code');
        const wantsEditMode = pathname.includes('/edit') && Boolean(code);

        let isEditMode = false;
        if (wantsEditMode && isLoggedIn) {
          const validateResult = await validatePaymentMethodChangeCode(code);
          if (!validateResult?.success) {
            await showError('Expired', validateResult?.error || 'Your verification link has expired. Please request a new link to update your banking details.');
          } else {
            isEditMode = true;
          }
        }

        const result = await getPaymentMethod();
        const hasExistingPaymentMethod = Boolean(result?.success && result?.data?.payer_name);

        if (hasExistingPaymentMethod && !isEditMode) {
          // User already has a payment method configured (and not in edit mode)
          setIsUserHasPaymentMethod(true);
          setPaymentMethod(result.data);
          return; // Do NOT call lookupUser / listServices in this case
        }

        // No existing payment method OR in edit mode – proceed with lookup + services
        runLookup();
      } catch (error) {
        // On error, fall back to original behavior so user can still set up payout
        runLookup();
      } finally {
        setIsLoadingPaymentMethod(false);
      }
    };

    initializePaymentAndLookup();
  }, [dispatch]);

  // Set user token from lookup user state
  useEffect(() => {
    if (lookupUserState?.data) {
      setUserToken(lookupUserState.data.data.user_token);
    }
  }, [lookupUserState]);

  // Build payer options for BANK_DEPOSIT services from non-crypto companies
  const bankDepositPayerOptions = useMemo(
    () => getBankDepositPayerOptions(user?.country, listServicesState),
    [user?.country, listServicesState]
  );

  const cryptoPayerOptions = useMemo(
    () => getCryptoPayerOptions(user?.country, listServicesState),
    [user?.country, listServicesState]
  );

  // Create dynamic schema based on masspay attributes
  const dynamicSchema = useMemo(() => {
    if (!allAttributes.length) {
      return null;
    }
    return createDynamicSchemaForBankAccount(allAttributes);
  }, [allAttributes]);

  // Create default values for dynamic form (only for required fields)
  const defaultValues = useMemo(() => {
    if (!allAttributes.length) {
      return {};
    }

    const defaults = {};
    const userPhone = user?.phone_number
      ? String(user.phone_number)
      : '';

    // Only include required (non-optional) fields to match schema and form rendering
    allAttributes
      .filter((attr) => !attr.is_optional)
      .forEach((attr) => {
        let value = '';

        // Autofill phone number fields when we already have the user's phone
        if (userPhone && attr.label && /phone/i.test(attr.label)) {
          value = userPhone;
        }

        defaults[attr.token] = value;
      });

    return defaults;
  }, [allAttributes, user?.phone_number]);

  // Dynamic form configuration - recreate when schema changes
  const dynamicForm = useForm({
    resolver: dynamicSchema ? zodResolver(dynamicSchema) : undefined,
    defaultValues,
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  // Reset form when default values change (when attributes are loaded)
  useEffect(() => {
    if (Object.keys(defaultValues).length > 0 && dynamicSchema) {
      dynamicForm.reset(defaultValues);
    }
  }, [defaultValues, dynamicSchema, dynamicForm]);

  const handleSetPayoutMethod = (method) => {
    setPayoutMethod(method);
    setSelectedPayerToken('');

    // Reset form and attributes when payout method / payer changes
    dynamicForm.reset({});
    setMasspayAttributes(null);
    setDestinationInfo(null);
  };

  const handleSetPayerToken = (payerToken) => {
    setSelectedPayerToken(payerToken || '');

    // Reset form and attributes when payout method / payer changes
    dynamicForm.reset({});
    setMasspayAttributes(null);
    setDestinationInfo(null);
  };

  // Keep destination token & currency in sync with services and selection
  useEffect(() => {
    if (!payoutMethod || !selectedPayerToken) {
      setDestinationInfo(null);
      return;
    }
    setMasspayAttributes(null);
    setDestinationInfo(null);

    const result = getDestinationToken(
      listServicesState,
      payoutMethod,
      selectedPayerToken
    );

    if (result) {
      setDestinationInfo({
        destinationToken: result.destinationToken,
        currencySymbol: result.currencySymbol,
        company_name: result.company_name,
        payer_name: result.payer_name
      });
    } else {
      setDestinationInfo(null);
      setMasspayAttributes(null);
    }
  }, [payoutMethod, selectedPayerToken, listServicesState]);

  useEffect(() => {
    if (!userToken || !destinationInfo?.destinationToken || !destinationInfo?.currencySymbol) {
      setMasspayAttributes(null);
      return;
    }

    const fetchMasspayAttributes = async () => {
      setIsLoadingAttributes(true);
      try {
        const result = await getMasspayAttributes({
          currency: destinationInfo.currencySymbol,
          destination_token: destinationInfo.destinationToken,
          user_token: userToken
        });

        if (result.success) {
          setMasspayAttributes(result.data);
          // Reset form with new default values (only for required fields)
          if (Array.isArray(result.data?.data)) {
            const newDefaults = {};
            const userPhone = user?.phone_number
              ? String(user.phone_number)
              : '';

            result.data.data
              .filter((attrSet) => Array.isArray(attrSet?.attributes))
              .flatMap((attrSet) => attrSet.attributes)
              .filter((attr) => !attr.is_optional)
              .forEach((attr) => {
                let value = '';

                // Autofill phone number fields when we already have the user's phone
                if (userPhone && attr.label && /phone/i.test(attr.label)) {
                  value = userPhone;
                }

                newDefaults[attr.token] = value;
              });

            dynamicForm.reset(newDefaults);
          }
        } else {
          await showError(
            'Error',
            result.error || 'Failed to load form fields'
          );
        }
      } catch (error) {
        await showError(
          'Error',
          error.message || 'An unexpected error occurred'
        );
      } finally {
        setIsLoadingAttributes(false);
      }
    };

    fetchMasspayAttributes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destinationInfo?.destinationToken, destinationInfo?.currencySymbol, userToken]);

  const onDynamicFormSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      // Extract and format account number or wallet address
      const accountOrWalletValue = extractAndFormatAccountOrWallet(
        payoutMethod,
        masspayAttributes,
        data
      );

      // Transform the form data to match the expected format
      const formData = {
        "user_token": userToken,
        "destination_token": destinationInfo?.destinationToken,
        "currency": destinationInfo?.currencySymbol,
        "company": destinationInfo?.company_name,
        "payer": destinationInfo?.payer_name,
        "accountOrAddress": accountOrWalletValue,
        "delivery_type": deliveryTypeMap[payoutMethod],
        "attributes": data ? Object.entries(data).map(([token, value]) => ({
          "token": token,
          "value": value
        })) : []
      }

      const result = await createMasspayAccount(formData);

      if (result.success) {
        await showSuccess('Success', 'Payout method saved!');
        navigate.toEarningsPayout();
      } else {
        await showError(
          'Error',
          result.error || 'Something went wrong. Please try again.'
        );
      }
    } catch (error) {
      await showError(
        'Error',
        error.message || 'An unexpected error occurred. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="modern-earnings-payout-page">
      <ModernHeader
        customTitle="Banking Details"
        showButtonBorder={false}
        hideSearchIcon
        showAvatar={false}
        showBackButton
      />

      {isLoadingPaymentMethod || lookupUserState?.loading || listServicesState?.loading ? (
        <div className="bank-account-loading-container">
          <i className="fas fa-spinner fa-spin bank-account-loading-spinner" />
          <p className="bank-account-loading-text">
            Loading information...
          </p>
        </div>
      ) : (
        <>
          {isUserHasPaymentMethod ? (
            <>
              <h2>Current Payout Method</h2>
              <PayoutMethodDisplay
                paymentMethod={paymentMethod}
                userEmail={user?.email}
              />
            </>
          ) : (
            <>
              <h2>Choose Payout Method</h2>

              {bankDepositPayerOptions.length > 0 && (
                <div
                  className={`cm_payout_method ${payoutMethod === PAYOUT_METHODS.BANK_ACH ? 'selected' : ''
                    }`}
                  onClick={() => handleSetPayoutMethod(PAYOUT_METHODS.BANK_ACH)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      handleSetPayoutMethod(PAYOUT_METHODS.BANK_ACH);
                    }
                  }}
                >
                  <div>
                    <span className="cm_span_radio" />
                  </div>
                  <div className="w-full">
                    <p>
                      <i className="fa-solid fa-building-columns" />
                      <b>Bank ACH</b>
                    </p>
                    <p>Direct bank transfer to your account</p>
                  </div>
                </div>
              )}

              {cryptoPayerOptions.length > 0 && (
                <div
                  className={`cm_payout_method ${payoutMethod === PAYOUT_METHODS.CRYPTO ? 'selected' : ''
                    }`}
                  onClick={() => handleSetPayoutMethod(PAYOUT_METHODS.CRYPTO)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      handleSetPayoutMethod(PAYOUT_METHODS.CRYPTO);
                    }
                  }}
                >
                  <div>
                    <span className="cm_span_radio" />
                  </div>
                  <div className="w-full">
                    <p>
                      <i className="fa-brands fa-bitcoin" />
                      <b>Cryptocurrency</b>
                    </p>
                    <p>Receive payments in digital currency</p>
                  </div>
                </div>
              )}

              {payoutMethod && (
                <div className="cm_payout_method">
                  <div className="w-full">
                    <p>
                      <b className="ml-0">
                        {payoutMethod === PAYOUT_METHODS.BANK_ACH
                          ? 'Bank Account Information'
                          : 'Cryptocurrency Settings'}
                      </b>
                    </p>
                    <p>
                      {payoutMethod === PAYOUT_METHODS.BANK_ACH
                        ? 'Enter your bank details to receive ACH transfers'
                        : 'Select your preferred network and currency'}
                    </p>
                  </div>
                </div>
              )}

              {payoutMethod && (
                <>
                  {payoutMethod === PAYOUT_METHODS.BANK_ACH &&
                    bankDepositPayerOptions?.length > 0 && (
                      <PayerSelectionDropdown
                        id="bank-deposit-payer"
                        label="Select Payer"
                        value={selectedPayerToken}
                        onChange={handleSetPayerToken}
                        options={bankDepositPayerOptions}
                      />
                    )}

                  {payoutMethod === PAYOUT_METHODS.CRYPTO &&
                    cryptoPayerOptions?.length > 0 && (
                      <PayerSelectionDropdown
                        id="crypto-payer"
                        label="Select Payer"
                        value={selectedPayerToken}
                        onChange={handleSetPayerToken}
                        options={cryptoPayerOptions}
                      />
                    )}

                  <form
                    key={
                      allAttributes.length
                        ? JSON.stringify(allAttributes.map((a) => a.token))
                        : 'no-attributes'
                    }
                    className="cm_dark_form"
                    onSubmit={dynamicForm.handleSubmit(onDynamicFormSubmit)}
                  >
                    {isLoadingAttributes ? (
                      <div className="bank-account-attributes-loading">
                        <i className="fas fa-spinner fa-spin" />
                        <p>Loading form fields...</p>
                      </div>
                    ) : allAttributes.length ? (
                      <DynamicMasspayForm
                        form={dynamicForm}
                        attributes={allAttributes}
                      />
                    ) : (
                      <div className="mb-3">
                        <p className="text-secondary">
                          {payoutMethod && destinationInfo?.destinationToken
                            ? 'No form fields available. Please try again.'
                            : ''}
                        </p>
                      </div>
                    )}

                    <div className="d-flex gap-3 mb-4 notice_card">
                      <div>
                        <i className="fa-solid fa-shield-halved" />
                      </div>
                      <div className="w-full">
                        <h4>Security Notice</h4>
                        <p>
                          Your banking information is fully encrypted and never stored
                          on Juicy House. As an added precaution, only cryptocurrency
                          addresses can be fully revealed.
                        </p>
                        <p>
                          Please note that only one payout method can be saved at a
                          time.
                        </p>
                      </div>
                    </div>

                    {payoutMethod && allAttributes.length > 0 && (
                      <div className="d-flex flex-column gap-3">
                        <button
                          type="submit"
                          className="btn btn_primary rounded_8"
                          disabled={isSubmitting || isLoadingAttributes}
                        >
                          {isSubmitting && (
                            <i className="fas fa-spinner fa-spin bank-account-submit-spinner" />
                          )}
                          {payoutMethod === PAYOUT_METHODS.BANK_ACH
                            ? 'Add Banking Details'
                            : 'Add Cryptocurrency Details'}
                        </button>
                        <button
                          type="button"
                          className="btn btn_dark_outline rounded_8"
                          onClick={() => navigate.back()}
                          disabled={isSubmitting}
                        >
                          Cancel
                        </button>
                      </div>
                    )}
                  </form>
                </>
              )}
            </>
          )}

          <hr className="cm_hr" />

          <div className="need_help_wrap">
            <h3>Need Help?</h3>
            <p
              onClick={() => navigate.to(ROUTES.FAQ + '#earnings-payouts')}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  navigate.to(ROUTES.FAQ + '#earnings-payouts');
                }
              }}
              style={{ cursor: 'pointer' }}
            >
              <i className="fa-solid fa-circle-question" />
              Earnings & Payouts FAQ
            </p>
            <p
              onClick={() => navigate.toContactSupport()}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  navigate.toContactSupport();
                }
              }}
              style={{ cursor: 'pointer' }}
            >
              <i className="fa-solid fa-headset" />
              Contact Support
            </p>
          </div>
        </>
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  user: state.user?.user,
  lookupUser: state.transaction?.lookupUser,
  listServices: state.transaction?.listServices
});

export default connect(mapStateToProps)(BankDetail);
